import React from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, ChevronRight } from 'lucide-react';

const ROUTE_LABELS: Record<string, string> = {
  admin: 'Admin',
  operations: 'Operations',
  fleet: 'Fleet',
  finance: 'Finance',
  people: 'People',
  trips: 'Trips',
  bookings: 'Bookings',
  parcels: 'Parcels',
  manifest: 'Manifest',
  vehicles: 'Vehicles',
  drivers: 'Drivers',
  routes: 'Routes',
  expenses: 'Expenses',
  withdrawals: 'Withdrawals',
  transactions: 'Transactions',
  users: 'Users',
};

const formatSegment = (raw: string): string => {
  if (ROUTE_LABELS[raw]) return ROUTE_LABELS[raw];
  if (/^\d+$/.test(raw)) return `#${raw}`;
  return raw.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

interface Props {
  userInitials: string;
  onOpenMenu: () => void;
}

const AdminTopbar: React.FC<Props> = ({ userInitials, onOpenMenu }) => {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  const display = segments.length > 1 ? segments.slice(1) : ['Dashboard'];

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6">
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label="Open menu"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <nav aria-label="Breadcrumb" className="min-w-0">
          <ol className="flex items-center gap-1 text-sm font-medium text-slate-600">
            {display.map((seg, idx) => {
              const isLast = idx === display.length - 1;
              return (
                <li key={`${seg}-${idx}`} className="flex items-center gap-1">
                  <span
                    className={
                      isLast
                        ? 'truncate font-semibold text-slate-900'
                        : 'truncate'
                    }
                  >
                    {formatSegment(seg)}
                  </span>
                  {!isLast && (
                    <ChevronRight
                      className="h-4 w-4 text-slate-400"
                      aria-hidden
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <span
          aria-hidden
          className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-800"
          title={userInitials}
        >
          {userInitials || '·'}
        </span>
      </div>
    </header>
  );
};

export default AdminTopbar;
